/**
 * The app's date field.
 *
 * MUI's picker is controlled: it shows whatever date its owner holds. Every
 * screen here validates inside `onChange` and returns early when the date is
 * out of range or still half-typed, which leaves that held date untouched — so
 * the field snapped back to it and the open calendar jumped to its month. That
 * is why browsing to another month kept returning to the current one, and why
 * typing a date never got past the first digit.
 *
 * So the date being typed or browsed lives here, and reaches the owner only
 * through the owner's own `onChange`. Each screen keeps its rule that its state
 * holds nothing it has not accepted; what the user sees no longer depends on
 * that rule.
 *
 * Two more things that were per-screen before:
 *
 * * **One unambiguous format, everywhere.** 9/10/2026 is the ninth of October
 *   to half the world and the tenth of September to the other half, and which
 *   one it meant followed the browser's locale rather than the app's. Dates
 *   read `09 Oct 2026` — `09 oct. 2026` in French — with the month in letters,
 *   so there is nothing left to misread. The empty field spells the format out,
 *   and the helper text shows it as an example.
 * * **Bounds are dates.** `MIN_DATE` is a string and so were most `minDate`
 *   props, where the adapter wants a Date; a `maxDate={new Date()}` written
 *   inline also handed the picker a new object on every render, which made it
 *   revalidate mid-typing. Both are normalised here, once, and kept stable.
 */

import { useEffect, useMemo, useState } from "react";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import type { DatePickerProps as MuiDatePickerProps } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import type { DateValidationError } from "@mui/x-date-pickers";
import { format as formatDate } from "date-fns";
import { enUS, fr } from "date-fns/locale";
import type { Locale } from "date-fns";
import { useTranslation } from "react-i18next";
import type { TextFieldProps } from "@mui/material";

/** Day, month in letters, four-digit year: unreadable as anything else. */
export const DATE_FORMAT = "dd MMM yyyy";

/** A day that is neither the 1st nor a 12th, so the example cannot be read backwards. */
const EXAMPLE_DATE = new Date(2026, 9, 9);

const LOCALES: Record<string, Locale> = { en: enUS, fr };

export const dateLocale = (language?: string): Locale =>
	LOCALES[(language || "en").split("-")[0]] ?? enUS;

/** What a screen may hand us: a Date, an ISO string, a timestamp, a Dayjs. */
export type DateBound = Date | string | number | { toDate: () => Date } | null;

/** Whatever it was, as a Date — or nothing, if it was not a date at all.
 *
 * Both adapters are installed (one table still uses Dayjs), so the picker's own
 * value type is a union of the two and anything may arrive here. */
export const asDate = (bound?: DateBound): Date | undefined => {
	if (bound === null || bound === undefined || bound === "") return undefined;
	let date: Date;
	if (bound instanceof Date) {
		date = bound;
	} else if (typeof (bound as { toDate?: () => Date }).toDate === "function") {
		date = (bound as { toDate: () => Date }).toDate();
	} else if (typeof bound === "string" && /^\d{4}-\d{2}-\d{2}$/.test(bound)) {
		const [year, month, day] = bound.split("-").map(Number);
		date = new Date(year, month - 1, day);
		if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
			return undefined;
		}
	} else {
		date = new Date(bound as string | number);
	}
	return date instanceof Date && !Number.isNaN(date.getTime()) ? date : undefined;
};

/** One Date per distinct instant, so the prop stops changing on every render. */
const useBound = (bound?: DateBound): Date | undefined => {
	const time = asDate(bound)?.getTime();
	return useMemo(() => (time === undefined ? undefined : new Date(time)), [time]);
};

export interface MoleDatePickerProps
	extends Omit<MuiDatePickerProps, "minDate" | "maxDate"> {
	minDate?: DateBound;
	maxDate?: DateBound;
}

export const MoleDatePicker = ({
	value,
	onChange,
	onClose,
	minDate,
	maxDate,
	format = DATE_FORMAT,
	slotProps,
	...rest
}: MoleDatePickerProps) => {
	const { t, i18n } = useTranslation();
	const locale = dateLocale(i18n.language);
	const min = useBound(minDate);
	const max = useBound(maxDate);

	const held = asDate(value as DateBound) ?? null;
	const heldTime = held ? held.getTime() : null;
	const [draft, setDraft] = useState<Date | null>(held);
	const [problem, setProblem] = useState<DateValidationError>(null);

	// Follow the owner when it sets the date itself: a reset button, a dialog
	// opened again, a value arriving from the server.
	useEffect(() => {
		setDraft(heldTime === null ? null : new Date(heldTime));
		setProblem(null);
	}, [heldTime]);

	const given =
		slotProps?.textField && typeof slotProps.textField === "object"
			? (slotProps.textField as TextFieldProps)
			: undefined;
	const hint = t("common:ui.hints.dateFormat", {
		example: formatDate(EXAMPLE_DATE, format, { locale }),
	});

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
			<MuiDatePicker
				{...rest}
				value={draft}
				format={format}
				minDate={min}
				maxDate={max}
				onChange={(next, context) => {
					setDraft(asDate(next as DateBound) ?? null);
					onChange?.(next, context);
				}}
				onError={(reason) => setProblem(reason)}
				onClose={() => {
					// Closing the calendar on a date nobody accepted would leave the
					// field showing one date and the screen holding another.
					if (problem !== null) {
						setDraft(held);
						setProblem(null);
					}
					onClose?.();
				}}
				slotProps={{
					...slotProps,
					textField: {
						...given,
						error: Boolean(given?.error) || problem !== null,
						helperText:
							problem !== null
								? t("common:validation.invalidDate")
								: given?.helperText || hint,
					},
				}}
			/>
		</LocalizationProvider>
	);
};

export { MoleDatePicker as DatePicker };
export default MoleDatePicker;
